// عُقْدَة · Uqda v2 — a redesign that keeps the node concept but
// rethinks proportions, personality and motion.
//
// Personality: curious + warm. A small, grounded knowledge-node that
// is genuinely alert — its orbiting ideas drift, energy flows inward
// along the tethers, and it blinks. It feels like a living point in
// the knowledge map, not a diagram.
//
// What changed vs v1:
//  • Satellites + tethers are ALIVE (gentle orbit, flowing energy, pulse)
//  • Tethers are stronger, color-matched to each idea-cluster
//  • Limbs rebalanced — weight matches the body, rounder feet
//  • Satellites re-placed out of the foot zone (no collisions)
//  • Body has depth (inner ring + light) and a blink
//  • Full emotion system: brows + eyes + mouth
//  • Clean tiny/icon mode that sheds detail gracefully

import React from 'react';
import './uqda.motion.css';

// satellite cluster layout — all kept in the upper hemisphere & sides
// so nothing ever collides with the legs/feet at the bottom.
const SATS = [
  { angle: -90,  dist: 72, r: 9,  c: "var(--emerald-light)", dur: "6.5s", amp: "8deg", delay: "0s",    pdur: "3.2s" },
  { angle: -32,  dist: 80, r: 11, c: "var(--amber)",         dur: "7.2s", amp: "7deg", delay: "-1.2s", pdur: "3.8s" },
  { angle: 26,   dist: 70, r: 7,  c: "var(--teal)",          dur: "6.0s", amp: "9deg", delay: "-.6s",  pdur: "2.8s" },
  { angle: -148, dist: 80, r: 10, c: "var(--c-ethics)",      dur: "7.6s", amp: "7deg", delay: "-2.1s", pdur: "4.1s" },
  { angle: 156,  dist: 70, r: 7,  c: "var(--violet)",        dur: "6.8s", amp: "8deg", delay: "-1.6s", pdur: "3.4s" },
  { angle: -114, dist: 86, r: 6,  c: "var(--teal-light)",    dur: "8.0s", amp: "6deg", delay: "-3.0s", pdur: "4.4s" },
];

const D2R = Math.PI / 180;

// ═══════════════════════════════════════════════════════════
// UQDA
// ═══════════════════════════════════════════════════════════
export function Uqda({
  size = 220,
  emotion = "calm",
  pose = "idle",            // "idle" | "walking" | "discovering"
  connections = 4,
  animate = true,
  color = "var(--emerald)",
  ink = "var(--ink)",
  paper = "var(--paper)",
  accent = "var(--amber)",
  className,
  style,
  ariaLabel,
  ...rest
}) {
  // detail level derived from size: tiny nodes shed legs + inner detail
  const tiny = size < 58;            // favicon-scale: just a clean node face
  const showLegs = size >= 92 && pose !== "discovering";
  const showInner = size >= 70;
  const walking = pose === "walking";
  const visible = tiny ? [] : SATS.slice(0, connections);

  const a11y = ariaLabel
    ? { role: "img", "aria-label": ariaLabel }
    : { "aria-hidden": "true" };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      fill="none"
      className={className}
      style={{ display: "block", overflow: "visible", ...style }}
      {...a11y}
      {...rest}
    >
      {/* ── SATELLITES + TETHERS (each in its own gently-orbiting group) ── */}
      {visible.map((s, i) => {
        const rad = s.angle * D2R;
        const bx = 100 + Math.cos(rad) * 38, by = 100 + Math.sin(rad) * 38;
        const sx = 100 + Math.cos(rad) * s.dist, sy = 100 + Math.sin(rad) * s.dist;
        const orbitStyle = { "--dur": s.dur, "--amp": s.amp, "--delay": s.delay, "--pdur": s.pdur };
        return (
          <g key={i} className={animate ? "uq-orbit" : ""} style={animate ? orbitStyle : undefined}>
            {/* base tether */}
            <line x1={bx} y1={by} x2={sx} y2={sy}
              stroke={ink} strokeWidth="1.3" opacity={pose === "sleeping" ? ".2" : ".28"}
              strokeDasharray={pose === "sleeping" ? "2 3" : "0"} />
            {/* energy flowing inward — colored to the idea-cluster */}
            {animate && pose !== "sleeping" && (
              <line className="uq-tether" x1={sx} y1={sy} x2={bx} y2={by}
                stroke={s.c} strokeWidth="2" strokeLinecap="round" opacity=".85" />
            )}
            {/* soft halo */}
            <circle className={animate ? "uq-sat-halo" : ""} style={animate ? orbitStyle : undefined}
              cx={sx} cy={sy} r={s.r + 3.5} fill={s.c} opacity=".16" />
            {/* the idea node */}
            <circle cx={sx} cy={sy} r={s.r} fill={s.c} stroke={ink} strokeWidth="1.4"
              opacity={pose === "sleeping" ? ".55" : "1"} />
            <circle cx={sx - s.r * .32} cy={sy - s.r * .32} r={s.r * .26} fill={paper} opacity=".45" />
          </g>
        );
      })}

      {/* ── DISCOVERY sparkle on the brightest idea ── */}
      {pose === "discovering" && !tiny && (
        <g className={animate ? "uq-spark" : ""}>
          {[0, 90, 180, 270, 45, 135, 225, 315].map((a, i) => {
            const rad = a * D2R;
            const r1 = i < 4 ? 16 : 12, r2 = i < 4 ? 24 : 18;
            return <line key={a}
              x1={148 + Math.cos(rad) * r1} y1={72 + Math.sin(rad) * r1}
              x2={148 + Math.cos(rad) * r2} y2={72 + Math.sin(rad) * r2}
              stroke={accent} strokeWidth="1.8" strokeLinecap="round" />;
          })}
        </g>
      )}

      {/* ── THE CREATURE (body + face + legs) bobs as one ── */}
      <g className={animate && !walking ? "uq-bob" : ""}>

        {/* LEGS — drawn first so the body sits on top of the leg roots */}
        {showLegs && walking && (
          <g>
            <g className={animate ? "uq-walk-leg-l" : ""}>
              <line x1="87" y1="132" x2="85" y2="166" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
              <circle cx="85" cy="168" r="4.6" fill={ink} />
            </g>
            <g className={animate ? "uq-walk-leg-r" : ""}>
              <line x1="113" y1="132" x2="115" y2="166" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
              <circle cx="115" cy="168" r="4.6" fill={ink} />
            </g>
          </g>
        )}
        {showLegs && !walking && pose !== "sleeping" && (
          <g>
            <line x1="88" y1="132" x2="86" y2="166" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
            <line x1="112" y1="132" x2="114" y2="166" stroke={ink} strokeWidth="3.4" strokeLinecap="round" />
            <circle cx="86" cy="168" r="4.6" fill={ink} />
            <circle cx="114" cy="168" r="4.6" fill={ink} />
          </g>
        )}
        {/* sleeping: tucked little feet */}
        {showLegs && pose === "sleeping" && (
          <g stroke={ink} strokeWidth="3" strokeLinecap="round">
            <line x1="90" y1="134" x2="98" y2="140" />
            <line x1="110" y1="134" x2="102" y2="140" />
          </g>
        )}

        {/* BODY */}
        <circle cx="100" cy="100" r="46" fill={color} opacity=".10" />
        <circle cx="100" cy="100" r="38" fill={color} stroke={ink} strokeWidth="2.4" />
        {showInner && <circle cx="100" cy="100" r="31" fill="none" stroke={paper} strokeWidth="1" opacity=".45" />}
        {/* soft top-light for volume */}
        {showInner && <ellipse cx="88" cy="84" rx="14" ry="9" fill={paper} opacity=".18" />}

        {/* FACE */}
        <Face emotion={emotion} pose={pose} ink={ink} paper={paper} accent={accent} animate={animate} tiny={tiny} />

        {/* ── READING — an open book held up, gently rocking ── */}
        {pose === "reading" && !tiny && (
          <g className={animate ? "uq-read" : ""}>
            {/* pages open toward the viewer (spine valley in the centre) */}
            <path d="M 100 138 L 72 132 L 74 150 L 100 150 Z" fill={paper} stroke={ink} strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M 100 138 L 128 132 L 126 150 L 100 150 Z" fill={paper} stroke={ink} strokeWidth="1.7" strokeLinejoin="round" />
            <line x1="100" y1="138" x2="100" y2="150" stroke={ink} strokeWidth="1.4" />
            <g stroke={ink} strokeWidth=".9" opacity=".5">
              <line x1="78" y1="139" x2="95" y2="138.5" />
              <line x1="78" y1="142.5" x2="94" y2="142" />
              <line x1="79" y1="146" x2="95" y2="145.5" />
              <line x1="105" y1="138.5" x2="122" y2="139" />
              <line x1="106" y1="142" x2="122" y2="142.5" />
              <line x1="105" y1="145.5" x2="121" y2="146" />
            </g>
          </g>
        )}

        {/* ── WRITING — a notepad held up in front, ink stroke drawing itself ── */}
        {pose === "writing" && !tiny && (
          <g transform="rotate(-4 100 146)">
            <rect x="64" y="132" width="72" height="28" rx="2" fill={paper} stroke={ink} strokeWidth="1.6" />
            <line x1="72" y1="140" x2="128" y2="140" stroke={ink} strokeWidth=".8" opacity=".3" />
            <path className={animate ? "uq-write" : ""} d="M 72 150 q 8 -6 16 0 t 16 0 t 16 0"
              fill="none" stroke={ink} strokeWidth="1.8" strokeLinecap="round" />
            {/* pen */}
            <g transform="rotate(34 128 142)">
              <rect x="112" y="140" width="26" height="4" rx="1.5" fill={accent} stroke={ink} strokeWidth="1" />
              <polygon points="138,140 146,142 138,144" fill={ink} />
              <rect x="108" y="140" width="4" height="4" rx="1" fill={ink} />
            </g>
          </g>
        )}

      </g>

      {/* SPEAKING — sound waves + speech bubble */}
      {pose === "speaking" && !tiny && (
        <g>
          {/* sound waves curling out from the mouth */}
          {[0, 1, 2].map(i => (
            <path key={i} className={animate ? "uq-wave" : ""}
              style={animate ? { "--wo": "118px 116px", "--wdelay": `${i * 0.45}s` } : undefined}
              d={`M 118 ${110 - i * 2} Q ${126 + i * 5} 116 118 ${122 + i * 2}`}
              fill="none" stroke={accent} strokeWidth="1.8" strokeLinecap="round" opacity=".8" />
          ))}
          {/* speech bubble (upper-right) */}
          <g>
            <rect x="120" y="34" width="58" height="38" rx="9" fill={paper} stroke={ink} strokeWidth="1.7" />
            <path d="M 126 70 L 116 84 L 134 70 Z" fill={paper} stroke={ink} strokeWidth="1.7" strokeLinejoin="round" />
            {/* a tiny knowledge-graph being spoken */}
            <line x1="135" y1="53" x2="149" y2="47" stroke={ink} strokeWidth="1" opacity=".5" />
            <line x1="149" y1="47" x2="163" y2="55" stroke={ink} strokeWidth="1" opacity=".5" />
            <line x1="135" y1="53" x2="148" y2="60" stroke={ink} strokeWidth="1" opacity=".5" />
            <circle cx="135" cy="53" r="3" fill="var(--teal)" stroke={ink} strokeWidth=".9" />
            <circle cx="149" cy="47" r="3.6" fill={color} stroke={ink} strokeWidth=".9" />
            <circle cx="163" cy="55" r="2.6" fill={accent} stroke={ink} strokeWidth=".9" />
            <circle cx="148" cy="60" r="2.4" fill="var(--violet)" stroke={ink} strokeWidth=".9" />
          </g>
        </g>
      )}

      {/* SLEEPING z's */}
      {pose === "sleeping" && !tiny && (
        <g>
          <text x="150" y="70" fontFamily="'Amiri', serif" fontStyle="italic" fontSize="15" fill={ink} opacity=".55">ز</text>
          <text x="166" y="52" fontFamily="'Amiri', serif" fontStyle="italic" fontSize="11" fill={ink} opacity=".35">ز</text>
        </g>
      )}

      {/* WALKING motion swoosh */}
      {walking && (
        <path d="M 58 178 Q 100 188 142 178" stroke={ink} strokeWidth="1" fill="none" opacity=".3" strokeDasharray="2 3" />
      )}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════
// FACE — brows + eyes + mouth, driven by emotion
// ═══════════════════════════════════════════════════════════
function Face({ emotion, pose, ink, paper, accent, animate, tiny }) {
  // tiny: just two confident dot-eyes (reads at favicon scale)
  if (tiny) {
    return (
      <g fill={ink}>
        <circle cx="90" cy="99" r="5" />
        <circle cx="110" cy="99" r="5" />
      </g>
    );
  }

  const blink = animate && pose !== "sleeping" ? "uq-blink" : "";
  const LX = 87, RX = 113, EY = 97;   // eye centers

  // ── per-emotion config ──
  const cfg = {
    calm:      { look: [0, 0],    open: 1,   brow: "none",  mouth: "smile" },
    curious:   { look: [0, -1.4], open: 1.1, brow: "raise1", mouth: "smallSmile" },
    happy:     { look: [0, 0],    open: 1,   brow: "raise2", mouth: "open" },
    thinking:  { look: [1.4, -1], open: .9,  brow: "furrow", mouth: "flatSm" },
    focused:   { look: [0, 1.6],  open: .85, brow: "lower",  mouth: "flat" },
    surprised: { look: [0, 0],    open: 1.5, brow: "raise2", mouth: "o" },
    sleeping:  { look: [0, 0],    open: 0,   brow: "none",   mouth: "rest" },
  }[emotion] || { look: [0, 0], open: 1, brow: "none", mouth: "smile" };

  const [dx, dy] = cfg.look;
  const rx = 5, ry = 6.6 * cfg.open;

  return (
    <g>
      {/* BROWS */}
      <Brows kind={cfg.brow} lx={LX} rx={RX} ink={ink} />

      {/* EYES */}
      {cfg.open === 0 ? (
        // sleeping — calm closed curves
        <g stroke={ink} strokeWidth="2" fill="none" strokeLinecap="round">
          <path d={`M ${LX - 5} ${EY} Q ${LX} ${EY + 4} ${LX + 5} ${EY}`} />
          <path d={`M ${RX - 5} ${EY} Q ${RX} ${EY + 4} ${RX + 5} ${EY}`} />
        </g>
      ) : (
        <g className={blink}>
          {[LX, RX].map((cx, i) => (
            <g key={i}>
              <ellipse cx={cx} cy={EY} rx={rx} ry={ry} fill={paper} stroke={ink} strokeWidth="1.7" />
              <circle cx={cx + dx} cy={EY + dy} r={2.5 * (cfg.open > 1.3 ? 1.15 : 1)} fill={ink} />
              <circle cx={cx + dx + .9} cy={EY + dy - 1.1} r=".8" fill={paper} />
            </g>
          ))}
        </g>
      )}

      {/* MOUTH — talking overrides the emotion mouth */}
      {pose === "speaking"
        ? <ellipse className={animate ? "uq-talk" : ""} cx="100" cy="116" rx="4" ry="3.6" fill={ink} />
        : <Mouth kind={cfg.mouth} ink={ink} accent={accent} />}
    </g>
  );
}

function Brows({ kind, lx, rx, ink }) {
  if (kind === "none") return null;
  const y = 84;
  const s = { stroke: ink, strokeWidth: 1.9, strokeLinecap: "round", fill: "none" };
  if (kind === "raise1") return (
    <g {...s}>
      <path d={`M ${lx - 5} ${y - 2} Q ${lx} ${y - 5} ${lx + 5} ${y - 3}`} />
      <path d={`M ${rx - 5} ${y + 1} Q ${rx} ${y - 1} ${rx + 5} ${y + 1}`} />
    </g>
  );
  if (kind === "raise2") return (
    <g {...s}>
      <path d={`M ${lx - 5} ${y - 1} Q ${lx} ${y - 4} ${lx + 5} ${y - 1}`} />
      <path d={`M ${rx - 5} ${y - 1} Q ${rx} ${y - 4} ${rx + 5} ${y - 1}`} />
    </g>
  );
  if (kind === "furrow") return (
    <g {...s}>
      <line x1={lx - 4} y1={y - 2} x2={lx + 5} y2={y + 1} />
      <line x1={rx + 4} y1={y - 2} x2={rx - 5} y2={y + 1} />
    </g>
  );
  if (kind === "lower") return (
    <g {...s}>
      <line x1={lx - 5} y1={y + 1} x2={lx + 5} y2={y + 2} />
      <line x1={rx - 5} y1={y + 2} x2={rx + 5} y2={y + 1} />
    </g>
  );
  return null;
}

function Mouth({ kind, ink, accent }) {
  const s = { stroke: ink, strokeWidth: 1.8, strokeLinecap: "round", fill: "none" };
  switch (kind) {
    case "smile":      return <path d="M 91 114 Q 100 121 109 114" {...s} />;
    case "smallSmile": return <path d="M 94 115 Q 100 119 106 115" {...s} />;
    case "open":       return <path d="M 92 113 Q 100 124 108 113 Q 100 118 92 113 Z" fill={ink} stroke={ink} strokeWidth="1.4" strokeLinejoin="round" />;
    case "flatSm":     return <path d="M 94 116 Q 99 117 105 115" {...s} />;
    case "flat":       return <line x1="93" y1="116" x2="107" y2="116" {...s} />;
    case "o":          return <ellipse cx="100" cy="116" rx="3.4" ry="4.2" fill={ink} />;
    case "rest":       return <line x1="95" y1="115" x2="105" y2="115" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".6" />;
    default:           return null;
  }
}

export default Uqda;
